import { useErrorInlineNotification } from '@/components/ErrorInlineNotification';
import { useGetPayeeList } from '@/features/ap_payees/components/DocInstructionMenu/Hooks/useGetPayeeList';
import { ListPayee } from '@/features/ap_payees/components/DocInstructionMenu/ListPayee';
import { useSendMails } from '@/features/ap_payees/components/DocInstructionMenu/SendInstructionsEmail/Contents/Hooks/useSendMails';
import {
  InstructionEmailDialogContentsProps,
  InstructionEmailForm,
} from '@/features/ap_payees/components/DocInstructionMenu/types';
import { useTranslation } from '@/i18n';
import { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useGetHeightTextArea } from './Hooks/useGetHeightTextArea';
import styles from './index.module.scss';
import { InstructionFooter } from './InstructionFooter';
import { InstructionInformation } from './InstructionInformation';
import { PreviewInstruction } from './PreviewInstruction';

interface InnerContentsProps extends InstructionEmailDialogContentsProps {
  ErrorContextHolder: JSX.Element;
  setError: (error: Error) => void;
}

const InnerContents: FC<InnerContentsProps> = ({
  onClose,
  payees,
  payeeQuery,
  ErrorContextHolder,
  setError,
}) => {
  const {
    payeeList,
    isFetchingNextPage,
    loadMorePayeeList,
    totalPayees,
    currentPreviewPayee,
  } = useGetPayeeList(payees, payeeQuery);

  const { t } = useTranslation();
  const { onSendMails, isPending } = useSendMails(payeeList, setError);
  const { textAreaRef, height } = useGetHeightTextArea();

  return (
    <>
      <div className={styles.container}>
        {ErrorContextHolder.props.visible && (
          <div className={styles.notification}>{ErrorContextHolder}</div>
        )}
        <ListPayee
          payees={payeeList}
          isFetchingNextPage={isFetchingNextPage}
          loadMorePayeeList={loadMorePayeeList}
          totalPayees={totalPayees}
          label={t('export_send_email_contact')}
        />
        <div className={styles.grid_container}>
          <div>
            <InstructionInformation textAreaRef={textAreaRef} />
          </div>
          <div>
            <PreviewInstruction
              currentPreviewPayee={currentPreviewPayee}
              height={height}
            />
          </div>
        </div>
      </div>
      <InstructionFooter
        onCancel={onClose}
        onSendEmails={onSendMails}
        isSendingEmail={isPending}
      />
    </>
  );
};

export const Contents: FC<InstructionEmailDialogContentsProps> = ({
  onClose,
  payees,
  payeeQuery,
}) => {
  const methods = useForm<InstructionEmailForm>({
    defaultValues: {
      content: '',
      subject: '',
      saveAsTemplate: false,
    },
    mode: 'all',
  });
  const [{ setError }, ErrorContextHolder, ErrorInlineNotificationProvider] =
    useErrorInlineNotification();
  return (
    <ErrorInlineNotificationProvider value={setError}>
      <FormProvider {...methods}>
        <InnerContents
          onClose={onClose}
          payees={payees}
          payeeQuery={payeeQuery}
          ErrorContextHolder={ErrorContextHolder}
          setError={setError}
        />
      </FormProvider>
    </ErrorInlineNotificationProvider>
  );
};
